import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { masterChartPath } from 'constants/masterChartRoutes';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  ArrowBackRounded,
  AddRounded,
  EditRounded,
  DeleteRounded,
  LocalHospitalRounded,
} from '@mui/icons-material';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import { contentCardSx } from 'styles/standaloneLayoutTokens';
import ConfirmDialog from 'components/common/ConfirmDialog';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import ProvenanceInfoButton from 'components/individual/health-records/ProvenanceInfoButton';
import { useAsyncAutocomplete } from 'hooks/useAsyncAutocomplete';
import { conditionsSearchService } from 'services/symptomCheck/conditionsSearchService';

const COMMON_CONDITIONS = [
  'Hypertension', 'Type 2 Diabetes', 'Type 1 Diabetes', 'Asthma', 'COPD',
  'Coronary Artery Disease', 'Heart Failure', 'Atrial Fibrillation',
  'Hyperlipidemia', 'Hypothyroidism', 'Depression', 'Anxiety',
  'GERD', 'Osteoarthritis', 'Rheumatoid Arthritis', 'Chronic Kidney Disease',
  'Migraine', 'Epilepsy', 'Sleep Apnea', 'Obesity',
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'error' },
  { value: 'controlled', label: 'Controlled', color: 'success' },
  { value: 'resolved', label: 'Resolved', color: undefined },
];

const MedicalConditionsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [conditions, setConditions] = useState([]);
  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: { condition: '', conditionCode: '', status: 'active', notes: '' },
  });

  const conditionValue = watch('condition');
  const searchConditions = useCallback(
    (q) => conditionsSearchService.searchConditionsWithCodes(q, 20),
    []
  );
  const { options: conditionOptions, loading: conditionOptionsLoading } =
    useAsyncAutocomplete(conditionValue, searchConditions, { minLength: 2 });

  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const doc = await db.collection('users').doc(user.uid).get();
        const hr = doc.data()?.healthRecords || {};
        setFullHealthRecords(hr);
        setConditions(hr.pmh || []);
      } catch {
        setLoadError(
          'Unable to load medical conditions — retry before assuming you have none.',
        );
        setConditions([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid]);

  const saveToFirestore = useCallback(
    async (updated) => {
      const merged = {
        ...(fullHealthRecords ?? {}),
        pmh: updated,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
    },
    [user?.uid, fullHealthRecords]
  );

  const openDialog = (index = -1) => {
    setEditingIndex(index);
    if (index >= 0) {
      const c = conditions[index];
      reset({
        condition: c.condition ?? '',
        conditionCode: c.conditionCode ?? '',
        status: c.status ?? 'active',
        notes: c.notes ?? '',
      });
    } else {
      reset({ condition: '', conditionCode: '', status: 'active', notes: '' });
    }
    setDialogOpen(true);
  };

  const onDialogSubmit = handleSubmit(async (data) => {
    setSaving(true);
    setError(null);
    try {
      const toSave = {
        condition: data.condition.trim(),
        status: data.status,
        notes: data.notes.trim(),
        ...(data.conditionCode ? { conditionCode: data.conditionCode } : {}),
      };
      const updated = [...conditions];
      if (editingIndex >= 0) {
        updated[editingIndex] = toSave;
      } else {
        updated.push(toSave);
      }
      await saveToFirestore(updated);
      setConditions(updated);
      setDialogOpen(false);
      setSuccessMsg(editingIndex >= 0 ? 'Condition updated.' : 'Condition added.');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const updated = conditions.filter((_, i) => i !== pendingDelete.index);
      setConditions(updated);
      await saveToFirestore(updated);
      setSuccessMsg(`Removed "${pendingDelete.label}".`);
      setPendingDelete(null);
    } catch {
      setError('Failed to remove. Please try again.');
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="inner">
      <Box sx={{ mt: { xs: 1, sm: 5 }, mb: 1 }}>
        <Button
          startIcon={<ArrowBackRounded />}
          onClick={() => navigate(masterChartPath())}
          size="small"
        >
          Back to Health Records
        </Button>
      </Box>
      <DashboardPageHeader
        title="Medical Conditions"
        subtitle="Chronic conditions and diagnoses"
        sx={{ mt: 0 }}
        actions={
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => openDialog()}
          >
            Add Condition
          </Button>
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...contentCardSx, mb: 2 }}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" gutterBottom>
              Conditions list unavailable until the load succeeds.
            </Typography>
          </Box>
        ) : conditions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <LocalHospitalRounded sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography color="text.secondary" gutterBottom>
              No conditions added yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add your chronic conditions and diagnoses.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Condition</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ width: 88 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {conditions.map((c, i) => {
                  const statusOpt = STATUS_OPTIONS.find((o) => o.value === c.status);
                  return (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>{c.condition}</Typography>
                          <ProvenanceInfoButton source={c.source} />
                        </Box>
                        {c.notes && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {c.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {statusOpt && (
                          <Chip
                            label={statusOpt.label}
                            size="small"
                            color={statusOpt.color}
                            variant={statusOpt.color ? 'filled' : 'outlined'}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton
                          size="small"
                          onClick={() => openDialog(i)}
                          aria-label={`Edit condition: ${c.condition}`}
                        >
                          <EditRounded fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setPendingDelete({ index: i, label: c.condition })}
                          aria-label={`Delete condition: ${c.condition}`}
                        >
                          <DeleteRounded fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingIndex >= 0 ? 'Edit Condition' : 'Add Medical Condition'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="condition"
              control={control}
              rules={{ required: 'Condition name is required' }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <Autocomplete
                  freeSolo
                  options={conditionOptions}
                  loading={conditionOptionsLoading}
                  getOptionLabel={(opt) =>
                    typeof opt === 'string' ? opt : opt?.displayName ?? ''
                  }
                  inputValue={field.value || ''}
                  onInputChange={(_, value, reason) => {
                    if (reason !== 'reset') {
                      field.onChange(value);
                      if (reason === 'clear') setValue('conditionCode', '');
                    }
                  }}
                  onChange={(_, newValue) => {
                    if (!newValue) return;
                    if (typeof newValue === 'string') {
                      field.onChange(newValue);
                    } else {
                      field.onChange(newValue.displayName ?? '');
                      setValue('conditionCode', newValue.conditionCode ?? '');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Condition"
                      placeholder="e.g., Hypertension"
                      error={!!fieldError}
                      helperText={
                        fieldError?.message ??
                        'Standard conditions support drug-disease interaction checks.'
                      }
                    />
                  )}
                />
              )}
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                gutterBottom
              >
                Common conditions:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {COMMON_CONDITIONS.slice(0, 8).map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setValue('condition', name);
                      setValue('conditionCode', '');
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>

            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select {...field} labelId="status-label" label="Status">
                    {STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Additional details (optional)"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={onDialogSubmit}
            loading={saving}
            disabled={saving}
          >
            {editingIndex >= 0 ? 'Update' : 'Add'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Remove Condition"
        message={`Remove "${pendingDelete?.label}"?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onExited={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default MedicalConditionsPage;
